/**
 * Roth conversion explorer (spec §10). Sweeps candidate fill targets —
 * bracket tops and IRMAA thresholds — through the conversion window (the gap
 * years between retirement and RMDs), evaluating the FULL plan for each:
 * IRMAA's two-year lookback, the ACA subsidy, the SS torpedo, and LTCG
 * stacking are all live in the projection, so their interactions are priced
 * in rather than approximated.
 *
 * Output is the whole candidate table plus a year-by-year schedule with each
 * year's marginal rate — not a single recommended number — and the breakeven
 * year, because conversions cost money now for a benefit later.
 */
import type { Plan } from '../model/types';
import { runProjection } from './projection';
import { CURRENT_YEAR } from '../model/defaults';
import { rmdStartAge } from './data/rmd';
import { IRMAA_TIERS_2026 } from './data/medicare';
import { ACCOUNT_META } from '../model/accountMeta';

export interface RothSchedule {
  year: number;
  amount: number;
  marginalRate: number;
  /** Conversion happened in a pre-Medicare ACA year (subsidy at risk). */
  duringAca: boolean;
}

export interface RothOutcome {
  key: string;
  label: string;
  rule?: NonNullable<Plan['decisions']['rothConversionRule']>;
  totalConverted: number;
  /** Today's dollars at the end of the plan. */
  endingNetWorth: number;
  /** Net worth with pre-tax balances discounted at the heirs' marginal rate. */
  estateValueAfterHeirTax: number;
  lifetimeTaxNominal: number;
  lifetimeTaxPV: number;
  /** First year the strategy's net worth overtakes doing nothing (if ever). */
  breakEvenYear?: number;
  /** Conversion years that collided with ACA subsidy years. */
  acaCollisionYears: number;
  schedule: RothSchedule[];
}

export interface RothExplorerResult {
  windowStart: number;
  windowEnd: number;
  baseline: RothOutcome;
  candidates: RothOutcome[];
  hasPretax: boolean;
  /** Conversions need a Roth account to land in — without one they no-op. */
  hasRothDestination: boolean;
}

function evaluate(
  plan: Plan,
  key: string,
  label: string,
  rule: RothOutcome['rule'] | undefined,
  heirMarginalRate: number,
  discountRate: number,
  baselineNetWorthByYear?: number[],
): RothOutcome {
  const variant: Plan = structuredClone(plan);
  variant.decisions.rothConversionRule = rule;
  const r = runProjection(variant, CURRENT_YEAR);

  const last = r.snapshots[r.snapshots.length - 1]!;
  const deflate = (v: number, f: number) => v / f;
  const endingNetWorth = deflate(last.netWorth, last.cumulativeInflationFactor);
  const estateValueAfterHeirTax = deflate(
    last.netWorth - last.balancesByFamily.pretax * heirMarginalRate,
    last.cumulativeInflationFactor,
  );

  let lifetimeTaxNominal = 0;
  let lifetimeTaxPV = 0;
  for (const s of r.snapshots) {
    lifetimeTaxNominal += s.totalTaxLiability;
    lifetimeTaxPV += s.totalTaxLiability / Math.pow(1 + discountRate, s.year - CURRENT_YEAR);
  }

  const schedule: RothSchedule[] = r.snapshots
    .filter((s) => s.rothConversionAmount > 0)
    .map((s) => ({
      year: s.year,
      amount: s.rothConversionAmount,
      marginalRate: s.federal.marginalOrdinaryRate,
      duringAca: s.acaFplRatio != null,
    }));

  let breakEvenYear: number | undefined;
  if (baselineNetWorthByYear && schedule.length > 0) {
    const firstConv = schedule[0]!.year;
    for (let i = 0; i < r.snapshots.length; i++) {
      const s = r.snapshots[i]!;
      if (s.year > firstConv && s.netWorth >= (baselineNetWorthByYear[i] ?? 0)) {
        breakEvenYear = s.year;
        break;
      }
    }
  }

  return {
    key,
    label,
    rule,
    totalConverted: schedule.reduce((t, s) => t + s.amount, 0),
    endingNetWorth,
    estateValueAfterHeirTax,
    lifetimeTaxNominal,
    lifetimeTaxPV,
    breakEvenYear,
    acaCollisionYears: schedule.filter((s) => s.duringAca).length,
    schedule,
  };
}

export function exploreRothConversions(
  plan: Plan,
  opts: { heirMarginalRate?: number; discountRate?: number } = {},
): RothExplorerResult {
  const heirRate = opts.heirMarginalRate ?? 0.24;
  const discount = opts.discountRate ?? 0.03;
  const people = plan.household.people;

  // The window: first retirement year through the year before RMDs begin.
  const windowStart = Math.max(
    CURRENT_YEAR,
    Math.min(...people.map((p) => p.birthYear + p.retirementAge)),
  );
  const windowEnd = Math.max(
    windowStart,
    Math.min(...people.map((p) => p.birthYear + rmdStartAge(p.birthYear) - 1)),
  );

  const hasPretax = plan.accounts.some(
    (x) => !x.inherited && ACCOUNT_META[x.type].taxFamily === 'pretax' && x.balance > 0,
  );
  const hasRothDestination = plan.accounts.some(
    (x) => !x.inherited && ACCOUNT_META[x.type].taxFamily === 'roth',
  );

  const clean: Plan = structuredClone(plan);
  clean.decisions.rothConversionRule = undefined;
  const baseline = evaluate(clean, 'none', 'No conversions', undefined, heirRate, discount);
  const baselineRun = runProjection(clean, CURRENT_YEAR);
  const baselineNW = baselineRun.snapshots.map((s) => s.netWorth);

  const joint = plan.household.filingStatus === 'mfj' || plan.household.filingStatus === 'qw';
  const tier1 = joint ? IRMAA_TIERS_2026[1]!.magiFloorMfj : IRMAA_TIERS_2026[1]!.magiFloorSingle;
  const tier2 = joint ? IRMAA_TIERS_2026[2]!.magiFloorMfj : IRMAA_TIERS_2026[2]!.magiFloorSingle;

  const specs: { key: string; label: string; rule: NonNullable<RothOutcome['rule']> }[] = [
    { key: 'b12', label: 'Fill the 12% bracket', rule: { type: 'bracket', target: 0.12, startYear: windowStart, endYear: windowEnd } },
    { key: 'b22', label: 'Fill the 22% bracket', rule: { type: 'bracket', target: 0.22, startYear: windowStart, endYear: windowEnd } },
    { key: 'b24', label: 'Fill the 24% bracket', rule: { type: 'bracket', target: 0.24, startYear: windowStart, endYear: windowEnd } },
    { key: 'irmaa1', label: 'Stay under the first IRMAA tier', rule: { type: 'irmaa', target: tier1, startYear: windowStart, endYear: windowEnd } },
    { key: 'irmaa2', label: 'Stay under the second IRMAA tier', rule: { type: 'irmaa', target: tier2, startYear: windowStart, endYear: windowEnd } },
  ];

  // Wait-for-Medicare variants. Every dollar converted before 65 inflates the
  // MAGI that sets the ACA subsidy, and crossing 400% of the poverty line
  // forfeits the whole credit — often more than the conversion saves. Starting
  // at 65 trades a shorter window for a collision-free one. Only offered when
  // 65 actually falls inside the window; otherwise these duplicate the rows
  // above.
  const medicareYear = Math.min(...people.map((p) => p.birthYear + 65));
  if (medicareYear > windowStart && medicareYear <= windowEnd) {
    specs.push(
      { key: 'b22at65', label: 'Fill the 22% bracket, but wait for Medicare at 65', rule: { type: 'bracket', target: 0.22, startYear: medicareYear, endYear: windowEnd } },
      { key: 'b24at65', label: 'Fill the 24% bracket, but wait for Medicare at 65', rule: { type: 'bracket', target: 0.24, startYear: medicareYear, endYear: windowEnd } },
    );
  }

  const candidates = specs.map((s) =>
    evaluate(plan, s.key, s.label, s.rule, heirRate, discount, baselineNW),
  );

  return { windowStart, windowEnd, baseline, candidates, hasPretax, hasRothDestination };
}
